import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { ToyClause, Game } from '../types';
import { toyClauseService } from '../services/api';
import { divertilandiaColors } from '../theme/divertilandiaColors';

interface ClausesManagerProps {
  game: Game;
  onClausesUpdated: (gameId: string) => void;
}

const ClausesManager: React.FC<ClausesManagerProps> = ({ game, onClausesUpdated }) => {
  const [allClauses, setAllClauses] = useState<ToyClause[]>([]);
  const [selectedClauseIds, setSelectedClauseIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Estados para crear/editar cláusulas
  const [openClauseDialog, setOpenClauseDialog] = useState(false);
  const [editingClause, setEditingClause] = useState<ToyClause | null>(null);
  const [clauseForm, setClauseForm] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    loadClauses();
    loadGameClauses();
  }, [game.id]);

  const loadClauses = async () => {
    try {
      const clauses = await toyClauseService.getAll();
      setAllClauses(clauses.filter(clause => clause.is_active));
    } catch (err) {
      console.error('Error loading clauses:', err);
      setError('Error al cargar las cláusulas');
    }
  };

  const loadGameClauses = async () => {
    try {
      const gameClauses = await toyClauseService.getGameClauses(game.id);
      setSelectedClauseIds(gameClauses.map(clause => clause.id));
    } catch (err) {
      console.error('Error loading game clauses:', err);
    }
  };

  const handleClauseToggle = (clauseId: string) => {
    setSelectedClauseIds(prev => 
      prev.includes(clauseId)
        ? prev.filter(id => id !== clauseId)
        : [...prev, clauseId]
    );
  };

  const handleSaveClauses = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await toyClauseService.assignClausesToGame(game.id, selectedClauseIds);
      setSuccess('Cláusulas asignadas exitosamente');
      onClausesUpdated(game.id);
    } catch (err) {
      console.error('Error saving clauses:', err);
      setError('Error al guardar las cláusulas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenClauseDialog = (clause?: ToyClause) => {
    if (clause) {
      setEditingClause(clause);
      setClauseForm({
        name: clause.name,
        description: clause.description,
        is_active: clause.is_active,
      });
    } else {
      setEditingClause(null);
      setClauseForm({
        name: '',
        description: '',
        is_active: true,
      });
    }
    setOpenClauseDialog(true);
  };

  const handleCloseClauseDialog = () => {
    setOpenClauseDialog(false);
    setEditingClause(null);
    setClauseForm({
      name: '',
      description: '',
      is_active: true,
    });
  };

  const handleSaveClause = async () => {
    if (!clauseForm.name.trim() || !clauseForm.description.trim()) {
      setError('El nombre y la descripción son obligatorios');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingClause) {
        await toyClauseService.update(editingClause.id, clauseForm);
        setSuccess('Cláusula actualizada exitosamente');
      } else {
        await toyClauseService.create(clauseForm);
        setSuccess('Cláusula creada exitosamente');
      }
      
      handleCloseClauseDialog();
      loadClauses();
    } catch (err) {
      console.error('Error saving clause:', err);
      setError('Error al guardar la cláusula');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClause = async (clauseId: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta cláusula?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await toyClauseService.delete(clauseId);
      setSuccess('Cláusula eliminada exitosamente');
      loadClauses();
      // Remover de seleccionadas si estaba seleccionada
      setSelectedClauseIds(prev => prev.filter(id => id !== clauseId));
    } catch (err) {
      console.error('Error deleting clause:', err);
      setError('Error al eliminar la cláusula');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Cláusulas para: {game.name}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => handleOpenClauseDialog()}
          sx={{ 
            borderColor: divertilandiaColors.standard.primary,
            color: divertilandiaColors.standard.primary,
          }}
        >
          Nueva Cláusula
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Selecciona las cláusulas que aplican a este juguete:
              </Typography>
            </FormLabel>
            
            <FormGroup>
              {allClauses.map((clause) => (
                <Box key={clause.id} sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedClauseIds.includes(clause.id)}
                          onChange={() => handleClauseToggle(clause.id)}
                          sx={{
                            color: divertilandiaColors.standard.primary,
                            '&.Mui-checked': {
                              color: divertilandiaColors.standard.primary,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {clause.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {clause.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', flex: 1 }}
                    />
                    
                    <Box display="flex" gap={1} ml={2}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenClauseDialog(clause)}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        Editar
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteClause(clause.id)}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        Eliminar
                      </Button>
                    </Box>
                  </Box>
                  
                  {selectedClauseIds.includes(clause.id) && (
                    <Chip
                      label="Seleccionada"
                      size="small"
                      sx={{
                        ml: 4,
                        mt: 1,
                        backgroundColor: divertilandiaColors.rainbow.green,
                        color: 'white',
                      }}
                    />
                  )}
                  
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
            </FormGroup>

            {allClauses.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                No hay cláusulas disponibles. Crea una nueva cláusula para comenzar.
              </Typography>
            )}
          </FormControl>

          <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveClauses}
              disabled={loading}
              sx={{
                backgroundColor: divertilandiaColors.standard.primary,
                '&:hover': {
                  backgroundColor: divertilandiaColors.rainbow.blue,
                },
              }}
            >
              {loading ? 'Guardando...' : 'Guardar Cláusulas'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Dialog para crear/editar cláusulas */}
      <Dialog 
        open={openClauseDialog} 
        onClose={handleCloseClauseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingClause ? 'Editar Cláusula' : 'Nueva Cláusula'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Nombre de la Cláusula"
              value={clauseForm.name}
              onChange={(e) => setClauseForm(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
              placeholder="Ej: Daño a bomba de aire"
            />
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Descripción"
              value={clauseForm.description}
              onChange={(e) => setClauseForm(prev => ({ ...prev, description: e.target.value }))}
              sx={{ mb: 2 }}
              placeholder="Ej: Si se daña la bomba de aire del brinca-brinca el costo a pagar por la misma será de $1,500 pesos."
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={clauseForm.is_active}
                  onChange={(e) => setClauseForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: divertilandiaColors.standard.primary,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: divertilandiaColors.standard.primary,
                    },
                  }}
                />
              }
              label="Cláusula activa"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseClauseDialog}
            startIcon={<CancelIcon />}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveClause}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
            sx={{
              backgroundColor: divertilandiaColors.standard.primary,
              '&:hover': {
                backgroundColor: divertilandiaColors.rainbow.blue,
              },
            }}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClausesManager;
